import { IconWithTextComponent } from "@kalyzee/escaet-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { Meeting, MeetingRoomAudienceType, MeetingRoomHighlightingState, MeetingRoomStatus, MeetingRoomType } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect } from "react";
import GroupConfiguratorExpertContainer from "../../../components/groups/expert/GroupConfiguratorExpertContainer";
import { GroupExpertContainer } from "../../../components/groups/expert/GroupExpertContainer";
import { GroupViewerExpertContainer, GroupViewerExpertContainerProps } from "../../../components/groups/expert/GroupViewerExpertContainer";
import { closeMeetingActivityRoom, openMeetingActivityRoom } from "../../../helpers/meeting";
import { useMeetingSelector } from "../../../hooks/meeting";
import { ExpertView, PopulatedMeetingData, PopulatedMeetingRoom } from "../../../interfaces/populatedData";
import { PedagogyContextState } from "../../../pages/master/pedagogy/context";
import { useUser } from "../../../store/session/hooks";
import styles from "./groupContainer.module.css";
import { MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";

export interface PedagogyGroupContainerProps {
  meetingContainerAPI?: MeetingContainerAPI;
  context: PedagogyContextState | undefined;
  meeting?: Meeting;
  populatedMeetingData?: PopulatedMeetingData;
  currentExpertView?: ExpertView;
  validators?: GroupViewerExpertContainerProps['validators'] & {
    onStopSpying?: (groupId: string) => boolean;
    onTalkToGroupLeaderFromGroup?: (group: PopulatedMeetingRoom) => boolean;
  }; 
  audioValidator?: (session: MeetingSessionOvenMediaReceiver) => boolean;
  style?: React.CSSProperties;
  className?: string;
}
export const PedagogyGroupContainer = ({
  meetingContainerAPI,
  meeting,
  populatedMeetingData,
  context,
  currentExpertView,
  validators,
  audioValidator,
  style,
  className,
}: PedagogyGroupContainerProps) => {
  const [session] = useMeetingSelector(meetingContainerAPI, (state) => state.session);
  const contextRef = useRefAutoUpdate(context);

  const user = useUser();

  const { users, rooms } = populatedMeetingData ?? {};

  const onStopSpying = () => {
    if (!meeting?.id || !context?.spiedGroupId) {
      return;
    }
    if (validators?.onStopSpying && !validators.onStopSpying(context.spiedGroupId)) return;
    context.setSpiedGroupId(undefined);
    openMeetingActivityRoom({
      id: meeting.id,
      audience: {
        type: MeetingRoomAudienceType.RoomMember,
        values: [context.spiedGroupId],
      },
    });
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onLeaveGroupVisibilityMode = useCallback(async () => {
    const meetingId = meeting?.id;
    if (!meetingId) return;
    context?.setSpiedGroupId(undefined);
    if (populatedMeetingData?.rooms.activityRoom?.room.status !== MeetingRoomStatus.Closed) {
      closeMeetingActivityRoom(meetingId);
    }
    /**
     * TODO :
     * Scnenario where we want to come back to the group we were at :
     * - we need to add a parameter "maintainEnlistedUsers" on the backend so we can update the room and keep the users
     * - we need to memorize on frontend in a state the fact we were in group mode before
     */
  }, [context, meeting, populatedMeetingData]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!audioValidator) return;
    const validator = contextRef.current?.addAudioValidator((session) => {
      return audioValidator(session);
    });
    return () => {
      validator?.flush();
    };
  }, [audioValidator]);

  useEffect(() => {
    return () => {
      contextRef.current?.setSpiedGroupId(undefined);
    }
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const renderNoGroup = (message: string = "Les groupes ne sont actuellement pas utilisés.") => {
    return <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", padding: "0px 40px" }}>{message}</div>;
  };

  const renderBackButton = () => {
    if (!currentExpertView) return null;
    const isVisibleInGroups = ["activity_one_group", "activity_one_group_spy", "activity_one_leader", "activity_all_leaders"].includes(currentExpertView.type);

    if (!isVisibleInGroups) return null;

    // User is in visibility mode, forced to be on the meeting group page,
    // Now he wants out, we need to remove the visiblity mode and he'll be automatically
    // redirected to dashboard
    let text = "Quitter le groupe";

    const activityRoom = populatedMeetingData?.rooms.activityRoom?.room;
    const audience = activityRoom?.audience;

    if (audience?.type === MeetingRoomAudienceType.RoomLeader) {
      if (audience.values === "*") {
        text = "Renvoyer à leurs groupes";
      } else {
        text = "Renvoyer à son groupe";
      }
    }

    return (
      <IconWithTextComponent
        text={text}
        style={{
          backgroundColor: "white",
          color: "black",
          padding: "1em 2em",
          gap: "0.5em",
          fontSize: "12px",
          margin: "0px 20px 20px 20px",
        }}
        onClick={onLeaveGroupVisibilityMode}
      />
    );
  };

  const renderContent = () => {
    if (!currentExpertView) return renderNoGroup();
    if (currentExpertView.type === "highlight") return renderNoGroup("Une montée au tableau est actuellement en cours.");
    if (["normal", "groups_enlistment"].includes(currentExpertView.type)) {
      if (!users || !user || !rooms) return renderNoGroup();
      return <GroupConfiguratorExpertContainer style={{ padding: 10, backgroundColor: "transparent" }} users={users} rooms={rooms} selfUserId={user.id} />;
    } else if (["group", "activity_one_group", "activity_one_group_spy", "activity_one_leader", "activity_all_leaders"].includes(currentExpertView.type)) {
      return (
        <>
          <GroupExpertContainer
            style={{ padding: 0, height: "unset", flex: 1, minHeight: 0 }}
            meetingContainerAPI={meetingContainerAPI}
            context={context}
            view={currentExpertView}
            validators={{
              onTalkToGroupLeader: validators?.onTalkToGroupLeaderFromGroup
            }}
            stopSpying={onStopSpying}
            showSelfVideo='current_video'
          />
          {renderBackButton()}
        </>
      );
    } else if (["groups_overview"].includes(currentExpertView.type)) {
      if (!users || !user || !rooms || !session) return renderNoGroup();
      return (
        <GroupViewerExpertContainer
          meetingContainerAPI={meetingContainerAPI}
          context={context}
          style={{ padding: 0 }}
          users={populatedMeetingData?.studentUsers ?? []}
          rooms={rooms}
          session={session}
          selfUserId={user.id}
          validators={validators}
        />
      );
    }
    // if one group is in highligth
    if (
      rooms?.groups?.find(
        (r) =>
          r.room.type === MeetingRoomType.Group && r.room.status === MeetingRoomStatus.Ongoing && r.room.highlighting === MeetingRoomHighlightingState.ONGOING
      )
    ) {
      return "Un groupe est actuellement au tableau...";
    }
    return renderNoGroup();
  };

  return (
    <div className={`${styles.container}${className ? ` ${className}` : ""}`} style={style}>
      {renderContent()}
    </div>
  );
};
